/**
 * Fit copy canonicalization — single source of truth for the 3 fit types.
 *
 * Product storage uses aliases (Turkish IDs `dar/normal/bol` and legacy
 * `slim/relaxed/loose/oversize/genis`). This module normalizes them to the
 * 3 canonical keys used by the tenant's fitLabels config (`slim/normal/relaxed`).
 *
 * When users edit copy on the Kalıp Bilgileri page, ANY place in the app that
 * shows fit copy should route through `getFitCopy()` so the edit propagates.
 */

// Product-fit alias → canonical fit type
export const FIT_CANONICAL = {
    slim: "slim", dar: "slim",
    normal: "normal",
    relaxed: "relaxed", loose: "relaxed", bol: "relaxed",
    genis: "relaxed", oversize: "relaxed",
};

// Fallback copy — matches backend DEFAULT_FIT_LABELS. Used ONLY when the
// tenant hasn't customized OR the fitLabels payload hasn't hydrated yet.
export const FIT_DEFAULTS = {
    slim:    { label: "Dar Kalıp",    advice: "Bir beden büyük almanızı öneririz." },
    normal:  { label: "Normal Kalıp", advice: "Kendi bedeninizi alabilirsiniz." },
    relaxed: { label: "Bol Kalıp",    advice: "Bir beden küçük almanızı öneririz." },
};

/** Reduce any product-fit alias to slim/normal/relaxed. Unknowns → "normal". */
export const canonicalizeFit = (rawFit) => FIT_CANONICAL[rawFit] || "normal";

/**
 * Return `{ label, advice }` for a product-fit value, honoring the tenant's
 * customized copy from Kalıp Bilgileri page. Falls back to defaults gracefully.
 */
export const getFitCopy = (fitLabels, rawFit) => {
    const canon = canonicalizeFit(rawFit);
    const custom = (fitLabels && fitLabels[canon]) || {};
    return {
        label:  custom.label  || FIT_DEFAULTS[canon].label,
        advice: custom.advice || FIT_DEFAULTS[canon].advice,
        canon,
    };
};

/**
 * Ordered list of the 3 fit presets — for chip selectors (e.g. ProductFitDialog).
 * Each entry: `{ storedAs, label, advice }` where `storedAs` is the alias saved
 * to sizing_products.fit (kept as Turkish for backward-compat with existing rows).
 */
export const buildFitOptions = (fitLabels) => [
    { storedAs: "dar",    ...getFitCopy(fitLabels, "dar") },
    { storedAs: "normal", ...getFitCopy(fitLabels, "normal") },
    { storedAs: "bol",    ...getFitCopy(fitLabels, "bol") },
];
